import { FormState, FormStateErrors } from '@constants/commonApp/types';
import { useEffect, useMemo, useState } from 'react';
import { getNewFormState, ValidatorType } from '../components/common/formUtils';

export const useApplicantFormSection = <T, K extends ValidatorType>(
  initialValues: T,
  validator: K | undefined,
  isInitiallySaved: boolean,
  onFormChange: (formState: FormState<T>) => void,
  additionalValidations?: (newFormState: FormState<T>) => FormStateErrors<T>,
) => {
  const initialFormState: FormState<T> = useMemo(
    () =>
      getNewFormState<T, typeof validator>({
        data: initialValues,
        validator,
        prevState: { isSaved: isInitiallySaved },
      }),
    [initialValues, isInitiallySaved],
  );

  const [formState, setFormState] = useState<FormState<T>>(initialFormState);
  useEffect(() => {
    if (isInitiallySaved) setFormState(initialFormState);
  }, [isInitiallySaved]);
  useEffect(() => onFormChange(formState), [formState]);

  const updateFormValues = (fieldUpdates: Partial<T>, errors?: FormStateErrors<T>) => {
    setFormState((prevState) => {
      const newFormState = getNewFormState<T, typeof validator>({
        data: fieldUpdates as T,
        prevState,
        initialValues,
        validator,
      });

      if (errors) newFormState.errors = { ...newFormState.errors, ...errors };

      if (additionalValidations) {
        const newErrors = additionalValidations?.(newFormState);

        newFormState.errors = {
          ...newFormState.errors,
          ...newErrors,
        };
      }

      return newFormState;
    });
  };

  return [formState, updateFormValues] as const;
};
